import React, { useEffect, useState } from 'react';
import {
  scaleMacroTotals,
  type EstimateConfidence,
  type MacroTotals,
} from '../../utils/nutritionMealGuards';

export interface ReviewableMealEstimate extends MacroTotals {
  fiber_g?: number;
  sodium_mg?: number;
  iron_mg?: number;
  vitamin_d_iu?: number;
}

interface MealEstimateReviewModalProps {
  isOpen: boolean;
  description: string;
  estimate: ReviewableMealEstimate | null;
  confidence: EstimateConfidence;
  logging?: boolean;
  onConfirm: (final: ReviewableMealEstimate) => void;
  onCancel: () => void;
}

const CONFIDENCE_COPY: Record<
  EstimateConfidence,
  { label: string; hint: string; tone: string; bg: string }
> = {
  high: {
    label: 'High confidence',
    hint: 'Branded item or explicit macros — quick check still recommended.',
    tone: '#166534',
    bg: '#DCFCE7',
  },
  medium: {
    label: 'Estimate — check portion',
    hint: 'Takeaway or mixed meal. Use Smaller/Larger if the portion looks off.',
    tone: '#92400E',
    bg: '#FEF3C7',
  },
  low: {
    label: 'Low confidence',
    hint: 'Vague portion or auto-adjusted. Please confirm before logging.',
    tone: '#991B1B',
    bg: '#FEE2E2',
  },
};

export default function MealEstimateReviewModal({
  isOpen,
  description,
  estimate,
  confidence,
  logging = false,
  onConfirm,
  onCancel,
}: MealEstimateReviewModalProps) {
  const [draft, setDraft] = useState<ReviewableMealEstimate | null>(estimate);
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    if (isOpen && estimate) {
      setDraft({ ...estimate });
      setShowManual(false);
    }
  }, [isOpen, estimate]);

  if (!isOpen || !draft) return null;

  const confidenceUi = CONFIDENCE_COPY[confidence];

  const applyScale = (multiplier: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const scaled = scaleMacroTotals(prev, multiplier);
      return { ...prev, ...scaled };
    });
  };

  const updateField = (field: keyof ReviewableMealEstimate, value: string) => {
    const parsed = Number.parseFloat(value);
    setDraft((prev) => (prev ? { ...prev, [field]: Number.isFinite(parsed) ? parsed : 0 } : prev));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="meal-review-title"
    >
      <div
        className="bg-[#FFFEF2] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="meal-review-title" className="text-xl font-bold text-[#2C2C2C] mb-2">
          Review estimate
        </h2>
        <p className="text-sm text-[#5A5A5A] italic mb-4 line-clamp-4">
          &ldquo;{description}&rdquo;
        </p>

        <div className="bg-white rounded-xl p-4 border border-[#D1D5DB] mb-3">
          <div className="text-3xl font-bold text-[#4A90E2] mb-1">
            {Math.round(draft.calories)} cal
          </div>
          <div className="text-base text-[#2C2C2C]">
            {Math.round(draft.protein_g)}p · {Math.round(draft.carbs_g)}c · {Math.round(draft.fat_g)}f
          </div>
        </div>

        <div
          className="rounded-lg p-3 mb-4"
          style={{ backgroundColor: confidenceUi.bg }}
        >
          <div className="font-bold text-sm mb-1" style={{ color: confidenceUi.tone }}>
            {confidenceUi.label}
          </div>
          <p className="text-sm text-[#4A4A4A] leading-snug">{confidenceUi.hint}</p>
        </div>

        <p className="text-sm font-semibold text-[#2C2C2C] mb-2">Quick adjust</p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <button
            type="button"
            onClick={() => applyScale(0.75)}
            className="bg-white border border-[#D1D5DB] rounded-lg py-3 text-xs font-semibold text-[#2C2C2C] hover:bg-gray-50"
          >
            Smaller
            <br />
            −25%
          </button>
          <button
            type="button"
            className="bg-[#E6F4FE] border border-[#4A90E2] rounded-lg py-3 text-xs font-semibold text-[#4A90E2]"
          >
            Looks right
          </button>
          <button
            type="button"
            onClick={() => applyScale(1.25)}
            className="bg-white border border-[#D1D5DB] rounded-lg py-3 text-xs font-semibold text-[#2C2C2C] hover:bg-gray-50"
          >
            Larger
            <br />
            +25%
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowManual((v) => !v)}
          className="text-[#4A90E2] font-semibold text-sm mb-3"
        >
          {showManual ? '▲ Hide manual edit' : '▼ Edit macros manually'}
        </button>

        {showManual && (
          <div className="space-y-2 mb-4">
            {(['calories', 'protein_g', 'carbs_g', 'fat_g'] as const).map((field) => (
              <div key={field} className="flex items-center gap-3">
                <label className="w-24 text-sm text-[#2C2C2C] capitalize">
                  {field === 'calories' ? 'Calories' : `${field.replace('_g', '')} (g)`}
                </label>
                <input
                  type="number"
                  className="flex-1 vintage-input"
                  value={Math.round(draft[field])}
                  onChange={(e) => updateField(field, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={logging}
            className="flex-1 py-3 rounded-lg border border-[#D1D5DB] bg-white text-[#5A5A5A] font-semibold hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(draft)}
            disabled={logging}
            className="flex-1 py-3 rounded-lg bg-[#4A90E2] text-white font-bold hover:bg-[#3a7bc8] disabled:opacity-70"
          >
            {logging ? 'Logging…' : 'Log meal ✓'}
          </button>
        </div>
      </div>
    </div>
  );
}
